import { Divider, Typography } from '@material-ui/core'

import { PriceRow } from '../../../../components/PriceRow'
import { getTranslate } from '../../../../localization'
import {
  BillingForBookingDetails,
  RoomStay
} from '../../../../types/bookingDetails'
import { PromotionApplication } from '../../../../types/promotion'
import { useStyles } from './styles/index.styles'

type Props = {
  billingInfo: BillingForBookingDetails | undefined
  promotionApplication?: PromotionApplication
  rooms?: RoomStay[]
}
const BillingSummary = ({
  billingInfo,
  promotionApplication,
  rooms = []
}: Props) => {
  const classes = useStyles()
  const appliedPromotions = promotionApplication?.applied ?? []
  const hasPromotionDiscount =
    appliedPromotions.length > 0 &&
    promotionApplication?.totalDiscountMinor !== '0' &&
    (billingInfo?.discount ?? 0) > 0

  const allocationRoomLabel = (roomStayReference: string) => {
    const roomIndex = rooms.findIndex(
      (room) => room.reservationNumber === roomStayReference
    )
    const room = rooms[roomIndex]

    return room
      ? `${getTranslate('Room Stay')} ${roomIndex + 1} — ${
          room.roomTypeName
        } (${room.ratePlanCode})`
      : getTranslate('Room stay {reference}', {
          reference: roomStayReference
        })
  }

  return (
    <div className={classes.container}>
      <div className={classes.top}>
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Final Billing Summary')}
        </Typography>
      </div>
      <Divider />
      <div className={classes.section}>
        {billingInfo?.roomsPrice.map((r, i) => (
          <PriceRow
            key={i}
            label={`${r.roomTypeName} (${r.ratePlanCode})`}
            value={r.price}
            currency={billingInfo?.curruncy}
          />
        ))}
        <PriceRow
          label={'Subtotal Before Discount'}
          value={billingInfo?.subTotalBeforeDiscount ?? 0}
          currency={billingInfo?.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.section}>
        {hasPromotionDiscount ? (
          appliedPromotions.map((promotion) => (
            <div key={promotion.promotionId}>
              <PriceRow
                label={promotion.name}
                translateLabel={false}
                minorAmount={promotion.discountMinor}
                currency={promotion.currency}
                isDiscount
              />
              {promotion.allocations.length > 0 && (
                <div>
                  <Typography variant="caption" color="textSecondary">
                    {getTranslate('Applied to')}
                  </Typography>
                  {promotion.allocations.map((allocation, index) => (
                    <PriceRow
                      key={`${allocation.roomStayReference}-${allocation.discountMinor}-${index}`}
                      label={`↳ ${allocationRoomLabel(
                        allocation.roomStayReference
                      )}`}
                      minorAmount={allocation.discountMinor}
                      currency={allocation.currency || promotion.currency}
                      isDiscount
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <PriceRow
            label={getTranslate('Discount')}
            value={billingInfo?.discount ?? 0}
            isDiscount
            currency={billingInfo?.curruncy}
          />
        )}

        <PriceRow
          label={'Subtotal After Discount'}
          value={billingInfo?.subTotalAfterDiscount ?? 0}
          currency={billingInfo?.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.section}>
        <PriceRow
          label={'Amendment Penalty Amount'}
          value={billingInfo?.amendmentPenaltyAmount ?? 0}
          currency={billingInfo?.curruncy}
        />
        <PriceRow
          label={'Cancelation Penalty Amount'}
          value={billingInfo?.cancelationPenaltyAmount ?? 0}
          currency={billingInfo?.curruncy}
        />
        <PriceRow
          label={'Refund Amount'}
          value={billingInfo?.refundAmount ?? 0}
          currency={billingInfo?.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.section}>
        <PriceRow
          label={'Final Amount'}
          value={billingInfo?.finalAmount ?? 0}
          currency={billingInfo?.curruncy}
          bold
        />
      </div>
    </div>
  )
}

export default BillingSummary
